"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.reportListResultSchema = exports.reportListRowSchema = exports.reportListColumnSchema = exports.reportSortSchema = exports.reportFiltersSchema = exports.NONE_FILTERS = exports.REPORT_FILTER_NONE = exports.REPORT_FILTERS_FOR = exports.REPORT_FILTER_KEYS = exports.METAFIELD_ENTITIES_FOR = exports.DEFAULT_REPORT_COLUMNS = exports.REPORT_COLUMNS = exports.REPORT_VOCABS = exports.REPORT_COLUMN_KINDS = exports.REPORT_LAYOUTS = void 0;
exports.metafieldColumnKey = metafieldColumnKey;
exports.metafieldIdOfColumn = metafieldIdOfColumn;
exports.reportColumn = reportColumn;
const zod_1 = require("zod");
const pagination_1 = require("../pagination");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · THE LIST LAYOUT AND ITS COLUMN CATALOGUE.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Product owner: *"We need a direct way for a report to show worklogs by tasks … a big table of all
 * the worklog hours on each task, not grouped by anything (but filterable to project, users, date
 * range etc.)"* and *"anything that exists for a reportable data type should have all of its values
 * as possible columns."*
 *
 * ⚠️⚠️ **RULE 8 · ADDED: A SECOND LAYOUT.** The mockup's builder only aggregates. `summary` is that
 * builder, untouched; `list` is one row per record of the source, with the columns below. A list
 * is never stored as a snapshot — it has no row limit, so a saved list report keeps its definition
 * and runs when it is opened (`0219`).
 *
 * ⚠️⚠️ **ONE CATALOGUE, FOUR READERS.** The builder offers these columns, the server knows how to
 * read each one, the CSV export writes them and the agent's report tool names them. A column added
 * to one reader and not the others is a column that is offered and then refused — so the KEYS live
 * here, once, and every reader looks them up.
 *
 * ⚠️ **The labels are the web catalogue's** (`report.col.<key>`, rule 7); the CSV header is the
 * `csv` slug here, which is how every other export names its columns.
 */
/**
 * ⚠️ 17 Oct 2026 · `timeline` — *Időbeli*: rows by periods, summed across the row
 * (`timeline.ts`).
 */
exports.REPORT_LAYOUTS = ['summary', 'list', 'timeline'];
/**
 * How a cell is FORMATTED, so the server sends a value and the screen words it.
 *
 * ⚠️ The text per kind: `hours` a plain decimal (`"7.5"`), `money` a plain decimal in the report's
 * currency (`"1200.50"`), `date` `YYYY-MM-DD`, `datetime` ISO 8601, `number` an integer, `bool`
 * `"true"`/`"false"`, `list` comma-separated, `enum` the machine value the screen translates through
 * `vocab`, `text` and `longtext` as they are (a description arrives as plain text, tags stripped).
 */
exports.REPORT_COLUMN_KINDS = [
    'text',
    'longtext',
    'date',
    'datetime',
    'hours',
    'money',
    'number',
    'bool',
    'list',
    'enum',
];
/** The fixed vocabularies an `enum` cell can hold — each has its words in the web catalogue. */
exports.REPORT_VOCABS = [
    'contractType',
    'contractStatus',
    'lineItemType',
    'lineItemScope',
    'lineItemStatus',
    /* 26 Oct 2026 · an allocation's `hard` / `soft`. */
    'booking',
];
const col = (key, kind, csv, extra = {}) => ({
    key,
    kind,
    csv,
    sortable: extra.sortable ?? true,
    ...(extra.money === undefined ? {} : { money: extra.money }),
    ...(extra.vocab === undefined ? {} : { vocab: extra.vocab }),
});
/** A task's own columns — on the task source, and again on every worklog through its task. */
const TASK_COLUMNS = [
    col('taskKey', 'text', 'feladat_kulcs'),
    col('taskTitle', 'text', 'feladat_cim'),
    col('taskDescription', 'longtext', 'feladat_leiras', { sortable: false }),
    col('taskStatus', 'text', 'feladat_allapot'),
    col('taskPriority', 'text', 'feladat_prioritas'),
    col('taskAssignee', 'text', 'felelos'),
    col('taskEstimate', 'hours', 'becsles'),
    col('taskStartsOn', 'date', 'kezdes'),
    col('taskDueOn', 'date', 'hatarido'),
    /* J-1 · the tracker's own facts, off the issue link (`0160`, `0217`). */
    col('taskComponents', 'list', 'komponensek', { sortable: false }),
    col('taskFixVersions', 'list', 'javitasi_verziok', { sortable: false }),
    col('taskEpic', 'text', 'epic', { sortable: false }),
];
/** Where a task sits — project, client, contract, line item. */
const PLACE_COLUMNS = [
    col('project', 'text', 'projekt'),
    col('client', 'text', 'ugyfel'),
    col('contract', 'text', 'szerzodes'),
    col('lineItem', 'text', 'sortetel'),
];
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ EVERY COLUMN EACH SOURCE CAN SHOW, IN THE ORDER THE PICKER OFFERS THEM.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Metafield columns are not listed here** — they are the workspace's own fields, keyed
 * `mf:<metafield id>`, and the server checks each against the entities this source reaches.
 */
exports.REPORT_COLUMNS = {
    worklog: [
        col('workedOn', 'date', 'datum'),
        col('hours', 'hours', 'ora'),
        col('member', 'text', 'rogzito'),
        col('team', 'text', 'csapat'),
        col('note', 'longtext', 'megjegyzes'),
        col('enteredAt', 'datetime', 'rogzitve'),
        col('overtimeType', 'text', 'tulora_tipus'),
        /* ⚠️ Priced at the rate card in force for that person ON THAT DAY — `cost-rows.ts`. */
        col('cost', 'money', 'koltseg', { sortable: false, money: true }),
        col('targetValue', 'money', 'eladasi_ertek', { sortable: false, money: true }),
        ...TASK_COLUMNS,
        ...PLACE_COLUMNS,
    ],
    task: [
        ...TASK_COLUMNS,
        col('taskLogged', 'hours', 'rogzitett_ora'),
        col('taskRemaining', 'hours', 'hatralevo_ora'),
        col('taskCreatedAt', 'datetime', 'letrehozva'),
        col('taskUpdatedAt', 'datetime', 'modositva'),
        ...PLACE_COLUMNS,
    ],
    contract: [
        col('contractCode', 'text', 'kod'),
        col('contractName', 'text', 'nev'),
        col('contractType', 'enum', 'tipus', { vocab: 'contractType' }),
        col('contractStatus', 'enum', 'allapot', { vocab: 'contractStatus' }),
        col('project', 'text', 'projekt'),
        col('client', 'text', 'ugyfel'),
        col('owner', 'text', 'felelos'),
        col('startsOn', 'date', 'kezdes'),
        col('endsOn', 'date', 'vege'),
        col('lineItemCount', 'number', 'sortetelek'),
        col('estimate', 'hours', 'becsles'),
        col('hours', 'hours', 'ora'),
        col('budget', 'money', 'budzse', { money: true }),
        col('revenue', 'money', 'bevetel', { money: true }),
        col('cost', 'money', 'koltseg', { money: true }),
        col('invoiced', 'money', 'kiszamlazva', { money: true }),
    ],
    lineitem: [
        col('lineItemNumber', 'number', 'sorszam'),
        col('lineItem', 'text', 'sortetel'),
        col('lineItemType', 'enum', 'tipus', { vocab: 'lineItemType' }),
        col('lineItemScope', 'enum', 'elszamolas', { vocab: 'lineItemScope' }),
        col('lineItemStatus', 'enum', 'allapot', { vocab: 'lineItemStatus' }),
        col('contract', 'text', 'szerzodes'),
        col('project', 'text', 'projekt'),
        col('client', 'text', 'ugyfel'),
        col('startsOn', 'date', 'kezdes'),
        col('endsOn', 'date', 'vege'),
        col('closedAt', 'datetime', 'lezarva'),
        col('syncTarget', 'text', 'szinkron_cel'),
        col('estimate', 'hours', 'becsles'),
        col('hours', 'hours', 'ora'),
        col('rate', 'money', 'oradij', { money: true }),
        col('budget', 'money', 'budzse', { money: true }),
        col('revenue', 'money', 'bevetel', { money: true }),
        col('cost', 'money', 'koltseg', { money: true }),
        col('invoiced', 'money', 'kiszamlazva', { money: true }),
    ],
    occurrence: [
        col('contract', 'text', 'szerzodes'),
        col('project', 'text', 'projekt'),
        col('client', 'text', 'ugyfel'),
        col('periodFrom', 'date', 'idoszak_kezdete'),
        col('periodTo', 'date', 'idoszak_vege'),
        col('hours', 'hours', 'ora'),
        col('budget', 'money', 'budzse', { money: true }),
        col('revenue', 'money', 'bevetel', { money: true }),
        col('cost', 'money', 'koltseg', { money: true }),
        col('invoiced', 'money', 'kiszamlazva', { money: true }),
    ],
    /* ⚠️ 17 Oct 2026 · Munkaidő has no list: a working day is a person's, and its figures only mean
       something added up over a period — the summary and the timeline draw it. */
    workday: [],
    /**
     * 26 Oct 2026 · Erőforrások — one row per allocation; `hours` is what it plans INSIDE the report's
     * period, and the dates are its own window.
     */
    allocation: [
        col('person', 'text', 'szemely'),
        ...PLACE_COLUMNS,
        col('startsOn', 'date', 'kezdes'),
        col('endsOn', 'date', 'vege'),
        col('hours', 'hours', 'tervezett_ora'),
        col('booking', 'enum', 'foglalas', { vocab: 'booking' }),
        col('note', 'longtext', 'megjegyzes', { sortable: false }),
    ],
};
/** What a new list report starts with — enough to read, few enough to add to. */
exports.DEFAULT_REPORT_COLUMNS = {
    worklog: ['workedOn', 'member', 'taskKey', 'taskTitle', 'project', 'hours', 'note'],
    task: [
        'taskKey',
        'taskTitle',
        'taskStatus',
        'taskAssignee',
        'project',
        'taskEstimate',
        'taskLogged',
    ],
    contract: [
        'contractCode',
        'contractName',
        'project',
        'contractStatus',
        'startsOn',
        'endsOn',
        'hours',
    ],
    lineitem: [
        'contract',
        'lineItemNumber',
        'lineItem',
        'lineItemType',
        'startsOn',
        'endsOn',
        'hours',
    ],
    occurrence: ['contract', 'periodFrom', 'periodTo', 'hours'],
    workday: [],
    allocation: ['person', 'project', 'lineItem', 'startsOn', 'endsOn', 'hours', 'booking'],
};
/**
 * ⚠️ **A metafield column is `mf:` and the field's uuid.** The prefix cannot collide with a
 * catalogue key, which are camelCase words.
 */
const METAFIELD_PREFIX = 'mf:';
function metafieldColumnKey(metafieldId) {
    return `${METAFIELD_PREFIX}${metafieldId}`;
}
/** The metafield id a column names, or null when it is a catalogue column. */
function metafieldIdOfColumn(key) {
    if (!key.startsWith(METAFIELD_PREFIX))
        return null;
    const id = key.slice(METAFIELD_PREFIX.length);
    return zod_1.z.uuid().safeParse(id).success ? id : null;
}
function reportColumn(source, key) {
    return exports.REPORT_COLUMNS[source].find((column) => column.key === key) ?? null;
}
/**
 * ⚠️ **Which metafield ENTITIES a source's rows reach.** A worklog reaches its own fields, its
 * task's and its project's; a line item its own, its contract's and its project's. The server
 * refuses a field of any other entity — it would be an empty column on every row.
 */
exports.METAFIELD_ENTITIES_FOR = {
    worklog: ['worklog', 'task', 'project'],
    task: ['task', 'project'],
    contract: ['contract', 'project', 'client'],
    lineitem: ['line_item', 'contract', 'project'],
    occurrence: ['contract', 'project'],
    workday: [],
    allocation: [],
};
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The list's filters — many values each, all optional.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Which filters a source honours** is `REPORT_FILTERS_FOR`. On a worklog `members` is who
 * LOGGED the time, on a task its assignee, on a money source the contract's owner — the same
 * duality the summary layout's `filterMemberId` already has, and for the same reason.
 *
 * ⚠️ **`statuses` are ids on the task-based sources and the `contract_status` enum on the money
 * sources**, because that is what a status is on each.
 */
exports.REPORT_FILTER_KEYS = [
    'projects',
    'clients',
    'members',
    /* R-60 · the person's teams — who logged, the assignee, whose days, whose plan. */
    'teams',
    'contracts',
    'lineItems',
    'statuses',
    'priorities',
];
exports.REPORT_FILTERS_FOR = {
    worklog: [
        'projects',
        'clients',
        'members',
        'teams',
        'contracts',
        'lineItems',
        'statuses',
        'priorities',
    ],
    task: [
        'projects',
        'clients',
        'members',
        'teams',
        'contracts',
        'lineItems',
        'statuses',
        'priorities',
    ],
    contract: ['projects', 'clients', 'members', 'contracts', 'statuses'],
    lineitem: ['projects', 'clients', 'members', 'contracts', 'lineItems', 'statuses'],
    occurrence: ['projects', 'clients', 'members', 'contracts', 'statuses'],
    /* 17 Oct 2026 · Munkaidő: whose days. */
    workday: ['members', 'teams'],
    /* 26 Oct 2026 · Erőforrások: whose plan, where. Soft bookings are their own switch. */
    allocation: ['projects', 'clients', 'members', 'teams', 'contracts', 'lineItems'],
};
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · *NONE* AS A FILTER VALUE — `'none'` beside the ids.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Kockázatkezelés's *Ismeretlen logolások* is a worklog list filtered to hours no line item holds.
 * So the project, client, contract and line item filters take `'none'` as one of their values:
 * *Nincs sortétel* alongside two line items reads "these two, or none at all".
 *
 * ⚠️⚠️ **A TASK WITH NO LINE ITEM IS NOT ALWAYS UNROUTED.** A recurring contract's tasks carry
 * `recurring_contract_id` instead, and their hours are billed. *Nincs sortétel* means neither — the
 * set Kockázatkezelés counted as unknown — and `report-list.ts` writes it that way.
 */
exports.REPORT_FILTER_NONE = 'none';
/** The filters that can say *none*. */
exports.NONE_FILTERS = [
    'projects',
    'clients',
    'contracts',
    'lineItems',
];
const filterValues = zod_1.z.array(zod_1.z.string().min(1).max(64)).max(200);
exports.reportFiltersSchema = zod_1.z.object({
    projects: filterValues.optional(),
    clients: filterValues.optional(),
    members: filterValues.optional(),
    teams: filterValues.optional(),
    contracts: filterValues.optional(),
    lineItems: filterValues.optional(),
    statuses: filterValues.optional(),
    priorities: filterValues.optional(),
    /** Free text over the row's names, keys and notes. */
    search: zod_1.z.string().max(200).optional(),
    /**
     * 26 Oct 2026 · the allocation source only: whether soft bookings count. Absent is `include` —
     * every planned hour; `exclude` matches Utilizáció, which leaves them out (UT-1).
     */
    softBookings: zod_1.z.enum(['include', 'exclude']).optional(),
});
exports.reportSortSchema = zod_1.z.object({
    column: zod_1.z.string().min(1).max(64),
    direction: zod_1.z.enum(['asc', 'desc']),
});
/** A column of a list result, as the server resolved it. */
exports.reportListColumnSchema = zod_1.z.object({
    key: zod_1.z.string(),
    kind: zod_1.z.enum(exports.REPORT_COLUMN_KINDS),
    vocab: zod_1.z.enum(exports.REPORT_VOCABS).nullable(),
    /** A metafield's own name — the workspace's words. Null for a catalogue column, which the screen words. */
    label: zod_1.z.string().nullable(),
    sortable: zod_1.z.boolean(),
});
exports.reportListRowSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** One per column, in the columns' order. Null is an empty cell. */
    cells: zod_1.z.array(zod_1.z.string().nullable()),
    /** The workspace-relative address the row opens, when it has one — a task, for the task-based sources. */
    path: zod_1.z.string().nullable(),
});
/**
 * ⚠️ **The list envelope every list uses** (`{ data, meta }`, CLAUDE.md §3), plus the columns and the
 * currency the money cells are in.
 */
exports.reportListResultSchema = zod_1.z.object({
    columns: zod_1.z.array(exports.reportListColumnSchema),
    data: zod_1.z.array(exports.reportListRowSchema),
    meta: pagination_1.listMetaSchema,
    currencyCode: zod_1.z.string(),
});
//# sourceMappingURL=columns.js.map